import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Device } from '../../lib/types';
import { useRescueContext } from '../../lib/RescueContext';
import { useToast } from '../../components/Toast/ToastContext';
import {
  RescueDeviceStatsData,
  rescueDeviceStatsQuery,
  RescueDevicesProgramData,
  rescueDevicesProgramQuery,
  RescueOrderShipmentsData,
  rescueOrderShipmentsQuery,
  rescueCreatePetFosterTransferMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
  rescueCancelPetAdoptionTransferMutation,
  rescueSendSmsMutation,
  rescueUnassignedDeviceInventoryQuery,
  RescueUnassignedDeviceInventoryData,
} from '../../lib/rescueQueries';
import useRescueDevices from '../../hooks/useRescueDevices';
import useAdoptedPetIds from '../../hooks/useAdoptedPetIds';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import DevicesTable from './DevicesTable';
import DevicesInTransitTable from './DevicesInTransitTable';
import PendingTransfersTable from './PendingTransfersTable';
import CompletedTransfersTable from './CompletedTransfersTable';
import AddPetModal from './AddPetModal';
import AddDeviceToPetModal from './AddDeviceToPetModal';
import TransferToFosterModal from './TransferToFosterModal';
import RecallDeviceModal from './RecallDeviceModal';
import TransferToAdopterModal from '../../components/TransferToAdopterModal/TransferToAdopterModal';
import ResendEmailModal from './ResendEmailModal';
import CancelTransferModal from './CancelTransferModal';
import SendSmsModal from './SendSmsModal';
import OrderDevicesModal from './OrderDevicesModal';
import DevicesEnrollModal from './DevicesEnrollModal';
import EnrollEmptyState from '../../components/EnrollEmptyState/EnrollEmptyState';
import {
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackDeviceResendEmail,
  trackDeviceCancelTransfer,
  trackDeviceSmsSent,
  trackDevicesOrderClicked,
  trackChipOwnerAdded,
} from '../../analytics';
import { useTransferPetWithChipToAdopter } from '../../lib/useTransferPetWithChipToAdopter';
import styles from './Devices.module.scss';

/** Page size for the inventory drain loop. Server clamps to its own max. */
const INVENTORY_PAGE_SIZE = 100;

/** Default page size for the server-side paginated sections. */
const PAGE_SIZE = 10;

/** Show completed adoptions from the last 90 days. */
const COMPLETED_SINCE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString();
})();

function LoadingSpinner({ message }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
      </svg>
      {message && <span>{message}</span>}
    </div>
  );
}

export default function Devices() {
  const { orgName } = useRescueContext();
  const {
    data: enrollmentData,
    loading: enrollmentLoading,
    refetch: refetchEnrollment,
  } = useQuery<RescueDevicesProgramData>(rescueDevicesProgramQuery);
  const isEnrolled = enrollmentData?.rescueDevicesProgram.enrolled ?? false;
  const { showToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const urlSearch = new URLSearchParams(location.search).get('search') || '';
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Sync local input when URL changes externally (e.g. navigating from dashboard)
  useEffect(() => {
    setSearchInput(urlSearch);
    setSearchQuery(urlSearch);
  }, [urlSearch]);

  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      history.replace({ search: params.toString() });
    }, 300);
  }

  // ── Org name (from context — resolves chipReseller or RESCUE household) ──

  const resellerName = orgName;

  // ── Stats (unfiltered counts for summary cards) ──

  const { data: statsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { completedSince: COMPLETED_SINCE },
  });
  const stats = statsData?.rescueDeviceStats;

  // ── Outstanding shipments (Devices in Transit) ──

  const { data: shipmentsData } = useQuery<RescueOrderShipmentsData>(rescueOrderShipmentsQuery);
  const shipments = shipmentsData?.rescueOrderShipments ?? [];

  // ── Unassigned Device Inventory ───────────
  //
  // The `rescueUnassignedDeviceInventory` query returns only delivered devices
  // not attached to a pet — server-filtered. We still page through with
  // fetchMore so the Unassigned section can show the full count even when the
  // server clamps a single page. Search is applied server-side via input.query
  // so the Unassigned section honors the search bar like Rescue/Foster do.

  const inventoryInput = useMemo(
    () => (searchQuery ? { query: searchQuery, limit: INVENTORY_PAGE_SIZE } : { limit: INVENTORY_PAGE_SIZE }),
    [searchQuery],
  );

  const {
    data: inventoryData,
    loading: inventoryLoading,
    error: inventoryError,
    refetch: refetchInventory,
    fetchMore: fetchMoreInventory,
  } = useQuery<RescueUnassignedDeviceInventoryData>(rescueUnassignedDeviceInventoryQuery, {
    variables: { input: inventoryInput },
    notifyOnNetworkStatusChange: true,
  });

  // Drain remaining pages whenever the cached result still has more.
  // The items.length >= totalCount guard prevents an infinite fetch loop if the
  // server ever reports hasMore=true without returning new items.
  //
  // Deps are scalar — depending on the whole `inventoryData` object would re-run
  // this effect on every Apollo cache write, including unrelated ones.
  const inventoryHasMore = inventoryData?.rescueUnassignedDeviceInventory?.hasMore ?? false;
  const inventoryItemsLength = inventoryData?.rescueUnassignedDeviceInventory?.items.length ?? 0;
  const inventoryTotalCount = inventoryData?.rescueUnassignedDeviceInventory?.totalCount ?? 0;

  // Two refs guard against searchQuery changing mid-pagination:
  //   - `inventoryInputRef` mirrors the active filter so `updateQuery` can
  //     compare the in-flight request's input against the now-current one and
  //     drop pages that arrived after the filter changed (otherwise Apollo
  //     would concatenate items from the old query into the new query's cache).
  //   - `paginatingRef` is a single-flight gate so we never have two fetchMore
  //     calls in flight for the same filter (relevant under StrictMode's
  //     double-invoke and back-to-back effect re-runs).
  // Both are reset whenever `inventoryInput` changes so the new filter starts
  // a fresh pagination.
  const inventoryInputRef = useRef(inventoryInput);
  const paginatingRef = useRef(false);
  useEffect(() => {
    inventoryInputRef.current = inventoryInput;
    paginatingRef.current = false;
  }, [inventoryInput]);

  useEffect(() => {
    if (paginatingRef.current) {
      return;
    }
    if (!inventoryHasMore || inventoryItemsLength >= inventoryTotalCount) {
      return;
    }
    const issuedInput = inventoryInput;
    // Release the gate when the in-flight request settles (or fails to launch).
    // Only clear if the filter is still ours; the input-change effect already
    // handled the gate for any new filter.
    function releaseGate() {
      if (issuedInput === inventoryInputRef.current) {
        paginatingRef.current = false;
      }
    }
    paginatingRef.current = true;
    let pending: Promise<unknown>;
    try {
      pending = fetchMoreInventory({
        variables: { input: { ...issuedInput, offset: inventoryItemsLength } },
        updateQuery: (prev, { fetchMoreResult }) => {
          // Drop the result if the active filter changed mid-flight —
          // otherwise we'd merge old-filter items into the new filter's cache.
          if (issuedInput !== inventoryInputRef.current) {
            return prev;
          }
          if (!fetchMoreResult) {
            return prev;
          }
          return {
            rescueUnassignedDeviceInventory: {
              ...fetchMoreResult.rescueUnassignedDeviceInventory,
              items: [
                ...prev.rescueUnassignedDeviceInventory.items,
                ...fetchMoreResult.rescueUnassignedDeviceInventory.items,
              ],
            },
          };
        },
      });
    } catch {
      // Apollo doesn't normally throw synchronously, but if it ever does we
      // must still release the gate or the loop wedges for the rest of the
      // session under this filter.
      releaseGate();
      return;
    }
    pending.finally(releaseGate);
  }, [inventoryHasMore, inventoryItemsLength, inventoryTotalCount, inventoryInput, fetchMoreInventory]);

  // ── Server-side paginated sections ────────

  const rescue = useRescueDevices('IN_RESCUE', searchQuery, PAGE_SIZE, resellerName);
  const foster = useRescueDevices('IN_FOSTER', searchQuery, PAGE_SIZE, resellerName);
  const pending = useRescueDevices('PENDING_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName);
  const completed = useRescueDevices('COMPLETED_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName, COMPLETED_SINCE);

  // Pet IDs of fully-adopted-out pets — used to hide mutation actions on their
  // device rows. While loading, treat rescue-row pets as potentially adopted so
  // we don't briefly show buttons we're about to hide.
  const { adoptedPetIds, loading: adoptedLoading } = useAdoptedPetIds();

  // ── Unassigned devices ──
  //
  // The query is named `rescueUnassignedDeviceInventory` and only returns
  // devices not attached to a pet, so no client-side filter is needed.

  const unassignedDevices = useMemo(() => inventoryData?.rescueUnassignedDeviceInventory?.items ?? [], [inventoryData]);

  // ── Loading / Error states ────────────────

  // Only show the full-page loading spinner on initial page load (no search active).
  // During search, Apollo may briefly have empty data for new variables — we don't
  // want to unmount the search bar (which would steal focus from the input).
  const initialLoading =
    !searchQuery &&
    rescue.loading &&
    foster.loading &&
    pending.loading &&
    rescue.devices.length === 0 &&
    foster.devices.length === 0 &&
    pending.devices.length === 0;
  const hasError = rescue.error && foster.error && pending.error && inventoryError;

  // ── T2A Stats ──────────────────────────

  const pendingCount = stats?.pendingAdoptionsCount ?? 0;
  const completedCount = stats?.completedAdoptionsCount ?? 0;
  const totalTransfers = pendingCount + completedCount;
  const showStats = totalTransfers > 0;

  const allTimeRate = totalTransfers > 0 ? Math.round((completedCount / totalTransfers) * 100) : 0;

  function rateColorClass(pct: number): string {
    if (pct >= 80) {
      return styles.summaryValueGreen;
    }
    if (pct >= 60) {
      return styles.summaryValueAmber;
    }
    return styles.summaryValueRed;
  }

  // ── GraphQL mutations ─────────────────────

  const mutationOpts = { refetchQueries: 'active' as const };

  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, mutationOpts);
  const [recallFosterTransfer] = useMutation(rescueRecallPetFosterTransferMutation, mutationOpts);
  const [unassignDevice] = useMutation(unassignDeviceMutation, mutationOpts);
  const [cancelAdoptionTransferMut] = useMutation(rescueCancelPetAdoptionTransferMutation, mutationOpts);
  const [sendSmsMut] = useMutation(rescueSendSmsMutation);
  const transferPetWithChip = useTransferPetWithChipToAdopter(mutationOpts);

  /** Reset all sections to page 1 after a mutation. */
  function resetAllPages() {
    rescue.resetPage();
    foster.resetPage();
    pending.resetPage();
    completed.resetPage();
  }

  // ── Helper: find device across all sections ──

  function findDevice(deviceId: string): Device | undefined {
    return (
      rescue.devices.find((d) => d.id === deviceId) ??
      foster.devices.find((d) => d.id === deviceId) ??
      pending.devices.find((d) => d.id === deviceId) ??
      completed.devices.find((d) => d.id === deviceId)
    );
  }

  // ── Modal state ──────────────────────────

  const [addPetOpen, setAddPetOpen] = useState(false);
  const [addPetDevice, setAddPetDevice] = useState<Device | null>(null);
  const [fosterOpen, setFosterOpen] = useState(false);
  const [fosterDevice, setFosterDevice] = useState<Device | null>(null);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallDevice, setRecallDevice] = useState<Device | null>(null);
  const [adopterOpen, setAdopterOpen] = useState(false);
  const [adopterDevice, setAdopterDevice] = useState<Device | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendDevice, setResendDevice] = useState<Device | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelDevice, setCancelDevice] = useState<Device | null>(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsDevice, setSmsDevice] = useState<Device | null>(null);
  const [assignPetOpen, setAssignPetOpen] = useState(false);
  const [assignPetModuleId, setAssignPetModuleId] = useState<string | null>(null);
  const [orderDevicesOpen, setOrderDevicesOpen] = useState(false);
  // Modal opens by default for users who haven't enrolled in the devices
  // program. Once dismissed it stays closed for the rest of the page lifetime;
  // the empty state's CTA re-opens it.
  const [enrollModalDismissed, setEnrollModalDismissed] = useState(false);
  const enrollModalOpen = !enrollmentLoading && !isEnrolled && !enrollModalDismissed;
  // ── Modal handlers ──────────────────────────

  function handleOpenAddPet(device: Device) {
    setAddPetDevice(device);
    setAddPetOpen(true);
  }

  function handleOpenFoster(device: Device) {
    setFosterDevice(device);
    setFosterOpen(true);
  }

  function handleOpenRecall(device: Device) {
    setRecallDevice(device);
    setRecallOpen(true);
  }

  function handleOpenAdopter(device: Device) {
    setAdopterDevice(device);
    setAdopterOpen(true);
  }

  function handleOpenResend(device: Device) {
    setResendDevice(device);
    setResendOpen(true);
  }

  function handleOpenCancel(device: Device) {
    setCancelDevice(device);
    setCancelOpen(true);
  }

  function handleOpenSms(device: Device) {
    setSmsDevice(device);
    setSmsOpen(true);
  }

  function handleOpenAssignPet(moduleId: string) {
    setAssignPetModuleId(moduleId);
    setAssignPetOpen(true);
  }

  // ── Mutation handlers ──────────────────────────

  function handleAddPet(_deviceId: string, petName: string) {
    void petName;
    showToast(`Pet name update is not yet supported via the API`);
  }

  async function handleTransferToFoster(
    deviceId: string,
    firstName: string,
    lastName: string,
    fosterEmail: string,
    fosterPhone: string,
  ) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId: device._petGraphqlId,
            email: fosterEmail,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: fosterPhone,
          },
        },
      });
      trackDeviceTransferToFoster('devices_page');
      resetAllPages();
      showToast(`${device.id} assigned to foster`);
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecall(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    if (device.holderType === 'foster') {
      // Foster recall — revoke the foster transfer
      try {
        await recallFosterTransfer({
          variables: {
            input: { petId: device._petGraphqlId },
          },
        });
        trackDeviceRecalled('devices_page');
        resetAllPages();
        showToast(`${device.id} recalled to rescue`);
      } catch (err) {
        showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } else {
      // Non-foster — just unassign the device from the pet
      try {
        await unassignDevice({
          variables: {
            moduleId: device.id,
            petId: device._petGraphqlId,
          },
        });
        resetAllPages();
        showToast(`Pet removed from ${device.id}`);
      } catch (err) {
        showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function handleTransferToAdopter(
    firstName: string,
    lastName: string,
    adopterEmail: string,
    adopterPhone: string,
  ) {
    const device = adopterDevice;
    if (!device) {
      return;
    }

    let result;
    try {
      result = await transferPetWithChip({
        petId: device._petGraphqlId,
        chipId: device.chip?.id ?? null,
        adopter: {
          firstName,
          lastName,
          email: adopterEmail,
          phone: adopterPhone,
          street: '',
          apt: '',
          city: '',
          state: '',
          zip: '',
        },
      });
      trackDeviceTransferToAdopt('devices_page');
      resetAllPages();
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return;
    }

    if (result.chipAssigned) {
      trackChipOwnerAdded('devices_t2a');
      showToast(`Transfer initiated for ${device.id}; chip ownership updated`);
    } else if (result.chipError) {
      const detail = result.chipError instanceof Error ? result.chipError.message : 'Unknown error';
      showToast(`Transfer initiated for ${device.id}, but chip transfer failed: ${detail}`);
    } else {
      showToast(`Transfer initiated for ${device.id}`);
    }
  }

  async function handleCancelAdoptionTransfer(device: Device) {
    try {
      await cancelAdoptionTransferMut({
        variables: {
          input: { petId: device._petGraphqlId },
        },
      });
      trackDeviceCancelTransfer('devices_page');
      resetAllPages();
      showToast(`Adoption transfer cancelled for ${device.id}`);
    } catch (err) {
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleSendSms(phoneNumber: string, message: string) {
    try {
      await sendSmsMut({ variables: { input: { phoneNumber, message } } });
      trackDeviceSmsSent();
      showToast('SMS sent successfully');
    } catch (err) {
      showToast(`Failed to send SMS: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleResendEmail(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    const to = device.adopterEmail ?? '';
    const subject = encodeURIComponent(`Set up your Fi collar${device.petName ? ` for ${device.petName}` : ''}`);
    const body = encodeURIComponent(
      `Hi${device.adopterName ? ` ${device.adopterName}` : ''},\n\nCongratulations on your adoption${
        device.petName ? ` of ${device.petName}` : ''
      }! Your new companion comes with a Fi GPS collar (${
        device.id
      }).\n\nTo activate the collar and start tracking, download the Fi app and follow the setup instructions:\nhttps://fitracking.com/app\n\nIf you have any questions, feel free to reach out to us or the Fi team at support@tryfi.com.\n\nBest,\n${orgName}`,
    );
    trackDeviceResendEmail();
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }

  // ── Order Devices ──────────────────────────

  function handleOrderDevices() {
    trackDevicesOrderClicked();
    setOrderDevicesOpen(true);
  }

  // ── Render ──────────────────────────

  if (enrollmentLoading) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner />
      </>
    );
  }

  if (!isEnrolled) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <EnrollEmptyState
          description="Not enrolled yet. Add GPS tracking to your rescue program to track fosters and reward adopters."
          ctaLabel="Set up now"
          onCta={() => setEnrollModalDismissed(false)}
        />
        <DevicesEnrollModal
          open={enrollModalOpen}
          onClose={() => setEnrollModalDismissed(true)}
          onEnrolled={() => {
            setEnrollModalDismissed(true);
            refetchEnrollment();
          }}
        />
      </>
    );
  }

  if (initialLoading) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner message="Loading devices..." />
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <div className={styles.errorContainer}>
          <p>Failed to load devices. Please try again.</p>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1>Fi Devices</h1>
        <div className={styles.headerActions}>
          <Button variant="green" onClick={handleOrderDevices}>
            <svg
              className={styles.headerBtnIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Order Devices
          </Button>
        </div>
      </div>

      {/* T2A Summary Stats */}
      {showStats && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Pending Transfers</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueAmber}`}>{pendingCount}</div>
            <div className={styles.summaryCardSubtext}>awaiting adopter activation</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Transfer to Adoption Rate</div>
            <div className={`${styles.summaryCardValue} ${rateColorClass(allTimeRate)}`}>{allTimeRate}%</div>
            <div className={styles.summaryCardSubtext}>
              {completedCount} completed, {pendingCount} pending
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Fi Pets</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueGreen}`}>{completedCount}</div>
            <div className={styles.summaryCardSubtext}>adopted through your rescue</div>
          </div>
        </div>
      )}

      {/* Search */}
      <SearchBar
        value={searchInput}
        onChange={handleSearchChange}
        placeholder="Search by device ID, pet name, or holder..."
      />

      {/* Pending Transfers */}
      <PendingTransfersTable
        devices={pending.devices}
        totalCount={pending.totalCount}
        currentPage={pending.currentPage}
        totalPages={pending.totalPages}
        pageSize={PAGE_SIZE}
        onPageChange={pending.goToPage}
        loading={pending.loading}
        skeletonCount={pending.skeletonCount}
        onResendEmail={handleOpenResend}
        onCancelTransfer={handleOpenCancel}
        onSendSms={handleOpenSms}
      />

      {/* Active Devices (Unassigned + Rescue + Foster) */}
      <DevicesTable
        rescueDevices={rescue.devices}
        fosterDevices={foster.devices}
        unassignedDevices={unassignedDevices}
        rescueTotalCount={rescue.totalCount}
        fosterTotalCount={foster.totalCount}
        rescueCurrentPage={rescue.currentPage}
        rescueTotalPages={rescue.totalPages}
        fosterCurrentPage={foster.currentPage}
        fosterTotalPages={foster.totalPages}
        pageSize={PAGE_SIZE}
        onRescuePageChange={rescue.goToPage}
        onFosterPageChange={foster.goToPage}
        rescueLoading={rescue.loading}
        fosterLoading={foster.loading}
        unassignedLoading={inventoryLoading}
        unassignedHasError={!!inventoryError}
        onRetryUnassigned={() => {
          refetchInventory();
        }}
        rescueSkeletonCount={rescue.skeletonCount}
        fosterSkeletonCount={foster.skeletonCount}
        searchQuery={searchQuery}
        adoptedPetIds={adoptedPetIds}
        adoptedLoading={adoptedLoading}
        onAddPet={handleOpenAddPet}
        onAssignUnassigned={handleOpenAssignPet}
        onTransferFoster={handleOpenFoster}
        onRecall={handleOpenRecall}
        onTransferAdopter={handleOpenAdopter}
        onSendSms={handleOpenSms}
      />

      {/* Devices in Transit (outstanding shipments to the rescue) */}
      <DevicesInTransitTable shipments={shipments} />

      {/* Completed Transfers */}
      <CompletedTransfersTable
        devices={completed.devices}
        totalCount={completed.totalCount}
        currentPage={completed.currentPage}
        totalPages={completed.totalPages}
        pageSize={PAGE_SIZE}
        onPageChange={completed.goToPage}
        loading={completed.loading}
        skeletonCount={completed.skeletonCount}
      />

      {/* Modals */}
      <AddPetModal
        open={addPetOpen}
        onClose={() => setAddPetOpen(false)}
        device={addPetDevice}
        onAddPet={handleAddPet}
      />
      <TransferToFosterModal
        open={fosterOpen}
        onClose={() => setFosterOpen(false)}
        device={fosterDevice}
        onTransfer={handleTransferToFoster}
      />
      <RecallDeviceModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        device={recallDevice}
        onRecall={handleRecall}
      />
      <TransferToAdopterModal
        open={adopterOpen}
        onClose={() => {
          setAdopterOpen(false);
          setAdopterDevice(null);
        }}
        deviceId={adopterDevice?.id ?? ''}
        petName={adopterDevice?.petName ?? null}
        chipSerialNumber={adopterDevice?.chip?.serialNumber ?? null}
        showRescueDiscount
        onTransfer={handleTransferToAdopter}
      />
      <ResendEmailModal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        device={resendDevice}
        onResend={handleResendEmail}
      />
      <CancelTransferModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        device={cancelDevice}
        onConfirm={handleCancelAdoptionTransfer}
      />
      <SendSmsModal
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        device={smsDevice}
        orgName={orgName}
        onSendSms={handleSendSms}
      />
      <AddDeviceToPetModal
        open={assignPetOpen}
        onClose={() => {
          setAssignPetOpen(false);
          setAssignPetModuleId(null);
        }}
        moduleId={assignPetModuleId}
      />
      <OrderDevicesModal open={orderDevicesOpen} onClose={() => setOrderDevicesOpen(false)} />
    </>
  );
}
